import { Link, Routes, Route, useNavigate, Navigate, useLocation, useParams } from 'react-router-dom'
import { useCallback, useEffect, useState, useRef } from 'react'
import { ArrowLeft, LogOut, Menu, PanelLeftClose, PanelLeftOpen, Store } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import SideNav, { closeAllNavGroups } from './components/SideNav.jsx'
import { SessionContext } from './session.js'
import ErrorBoundary from './components/ErrorBoundary.jsx'
import Home from './pages/Home.jsx'
import PlanSettings from './pages/settings/PlanSettings.jsx'
import Dashboard from './pages/Dashboard.jsx'
import Analysis from './pages/Analysis.jsx'
import AccountList from './pages/master/AccountList.jsx'
import AccountForm from './pages/master/AccountForm.jsx'
import OpeningBalances from './pages/master/OpeningBalances.jsx'
import OperatingMenu from './pages/operating/Menu.jsx'
import ExpenseList from './pages/operating/ExpenseList.jsx'
import ExpenseForm from './pages/operating/ExpenseForm.jsx'
import ExpenseDetail from './pages/operating/ExpenseDetail.jsx'
import Assets from './pages/accounting/Assets.jsx'
import AssetForm from './pages/accounting/AssetForm.jsx'
import AssetDetail from './pages/accounting/AssetDetail.jsx'
import PrepaidList from './pages/accounting/PrepaidList.jsx'
import PrepaidForm from './pages/accounting/PrepaidForm.jsx'
import ImportMasters from './pages/import/ImportMasters.jsx'
import MasterMenu from './pages/master/MasterMenu.jsx'
import ItemList from './pages/master/ItemList.jsx'
import ItemForm from './pages/master/ItemForm.jsx'
import VendorList from './pages/master/VendorList.jsx'
import VendorForm from './pages/master/VendorForm.jsx'
import LocationList from './pages/master/LocationList.jsx'
import LocationForm from './pages/master/LocationForm.jsx'
import CustomerList from './pages/master/CustomerList.jsx'
import CustomerForm from './pages/master/CustomerForm.jsx'
import DefaultAccounts from './pages/master/DefaultAccounts.jsx'
import UserList from './pages/master/UserList.jsx'
import UserForm from './pages/master/UserForm.jsx'
import UserDetail from './pages/master/UserDetail.jsx'
import AccountSetup from './pages/master/AccountSetup.jsx'
import AccountingMenu from './pages/accounting/AccountingMenu.jsx'
import JournalEntries from './pages/accounting/JournalEntries.jsx'
import ReportsMenu from './pages/accounting/ReportsMenu.jsx'
import TrialBalance from './pages/accounting/TrialBalance.jsx'
import BalanceSheet from './pages/accounting/BalanceSheet.jsx'
import IncomeStatement from './pages/accounting/IncomeStatement.jsx'
import PeriodReportPrint from './pages/accounting/PeriodReportPrint.jsx'
import GeneralLedger from './pages/accounting/GeneralLedger.jsx'
import BankRec from './pages/accounting/BankRec.jsx'
import MonthEnd from './pages/accounting/MonthEnd.jsx'
import StatementClasses from './pages/accounting/StatementClasses.jsx'
import JournalEntryDetail from './pages/accounting/JournalEntryDetail.jsx'
import TransactionTypes from './pages/accounting/TransactionTypes.jsx'
import TransactionTypeForm from './pages/accounting/TransactionTypeForm.jsx'
import RecordTransaction from './pages/accounting/RecordTransaction.jsx'
import APSubledger from './pages/accounting/APSubledger.jsx'
import VendorInvoiceMatching from './pages/accounting/VendorInvoiceMatching.jsx'
import ARSubledger from './pages/accounting/ARSubledger.jsx'
import CompanySettings from './pages/settings/CompanySettings.jsx'
import MonoConnections from './pages/settings/MonoConnections.jsx'
import Payments from './pages/settings/Payments.jsx'  // NEW
import SettingsMenu from './pages/settings/SettingsMenu.jsx'
import DataTools from './pages/settings/DataTools.jsx'
import SalesMenu from './pages/sales/SalesMenu.jsx'
import POS from './pages/sales/POS.jsx'
import SaleList from './pages/sales/SaleList.jsx'
import RefundLookup from './pages/sales/RefundLookup.jsx'
import RefundProcess from './pages/sales/RefundProcess.jsx'
import SaleDetail from './pages/sales/SaleDetail.jsx'
import CustomerQuoteList from './pages/sales/CustomerQuoteList.jsx'
import JobList from './pages/sales/JobList.jsx'
import JobDetail from './pages/sales/JobDetail.jsx'
import CuttingList from './pages/sales/CuttingList.jsx'
import JobsInProgress from './pages/accounting/JobsInProgress.jsx'

function OldJobLink() {
  const { jobId } = useParams()
  return <Navigate to={`/jobs/${jobId}`} replace />
}
import CustomerQuoteForm from './pages/sales/CustomerQuoteForm.jsx'
import CustomerInvoiceList from './pages/sales/CustomerInvoiceList.jsx'
import CustomerInvoiceForm from './pages/sales/CustomerInvoiceForm.jsx'
import CustomerPayment from './pages/sales/CustomerPayment.jsx'
import PurchaseMenu from './pages/purchase/PurchaseMenu.jsx'
import POList from './pages/purchase/POList.jsx'
import POForm from './pages/purchase/POForm.jsx'
import PODetail from './pages/purchase/PODetail.jsx'
import ReceiptList from './pages/purchase/ReceiptList.jsx'
import ReceiptDetail from './pages/purchase/ReceiptDetail.jsx'
import ReceiptForm from './pages/purchase/ReceiptForm.jsx'
import VendorReturnList from './pages/purchase/VendorReturnList.jsx'
import VendorReturnDetail from './pages/purchase/VendorReturnDetail.jsx'
import VendorReturnForm from './pages/purchase/VendorReturnForm.jsx'
import StockValue from './pages/purchase/StockValue.jsx'
import InvoiceList from './pages/purchase/InvoiceList.jsx'
import InvoiceForm from './pages/purchase/InvoiceForm.jsx'
import InvoiceDetail from './pages/purchase/InvoiceDetail.jsx'
import POApprovalInbox from './pages/purchase/POApprovalInbox.jsx'
import ApprovalSettings from './pages/purchase/ApprovalSettings.jsx'
import APLedger from './pages/purchase/APLedger.jsx'
import APVendorDetail from './pages/purchase/APVendorDetail.jsx'
import APPayment from './pages/purchase/APPayment.jsx'
import InventoryMenu from './pages/inventory/InventoryMenu.jsx'
import StockList from './pages/inventory/StockList.jsx'
import ProductStock from './pages/inventory/ProductStock.jsx'
import AdjustStock from './pages/inventory/AdjustStock.jsx'
import MoveStock from './pages/inventory/MoveStock.jsx'
import StockMovements from './pages/inventory/StockMovements.jsx'
import StorageAreas from './pages/inventory/StorageAreas.jsx'
import BinStockBySku from './pages/inventory/BinStockBySku.jsx'
import ValuationApprovals from './pages/inventory/ValuationApprovals.jsx'
import BinMoveBySku from './pages/inventory/BinMoveBySku.jsx'
import BinAdjustBySku from './pages/inventory/BinAdjustBySku.jsx'
import CostLayers from './pages/inventory/CostLayers.jsx'
import Production from './pages/inventory/Production.jsx'
import ProductionRun from './pages/inventory/ProductionRun.jsx'
import ProductionProducts from './pages/inventory/ProductionProducts.jsx'
import ProductionCogm from './pages/inventory/ProductionCogm.jsx'
import CostLayerSummary from './pages/inventory/CostLayerSummary.jsx'
import StockCounts from './pages/inventory/StockCounts.jsx'
import StockCountDetail from './pages/inventory/StockCountDetail.jsx'
import AuditTrail from './pages/utilities/AuditTrail.jsx'
import Login from './pages/auth/Login.jsx'
import Signup from './pages/auth/Signup.jsx'
import ForgotPassword from './pages/auth/ForgotPassword.jsx'
import ResetPassword from './pages/auth/ResetPassword.jsx'
import OnboardingPage from './pages/OnboardingPage.jsx'
import PlatformTenants from './pages/platform/PlatformTenants.jsx'
import Clients from './pages/practice/Clients.jsx'
import AddClient from './pages/practice/AddClient.jsx'
import Staff from './pages/practice/Staff.jsx'
import StaffForm from './pages/practice/StaffForm.jsx'
import PracticeSignup from './pages/practice/PracticeSignup.jsx'
import ForAccountants from './pages/ForAccountants.jsx'
import InviteAccept from './pages/practice/InviteAccept.jsx'
import AccountantSettings from './pages/settings/AccountantSettings.jsx'
import { backToPractice } from './api/practice'
import { me as getMe, logout as apiLogout } from './api/auth'
import { setCompanyZone, setCurrency } from './utils/format'
import { deviceZone } from './utils/timeZones'
import { setPlanModules } from './utils/onboarding'
import { hasModule, paymentsOnly } from './session'
import { useCreateOptions } from './components/CreateOption'
import NotInPlan from './pages/NotInPlan'
import { scanPOApprovals } from './api/purchase'
import { listCounts, listValuationApprovals } from './api/inventory'
import { getCompany, getConfig, timeZoneFromDevice } from './api/settings'
import { IN_APP } from './inApp'

// ---- Role helpers -----------------------------------------------------------
const ROLE_SYNONYMS = {
  // Accounting
  accountant: 'accounting',
  accounting: 'accounting',

  // Purchasing
  purchaser: 'purchase',
  purchasing: 'purchase',

  // Operating / CAPEX & OPEX
  'operating cost': 'operating_cost',
  'capex & opex': 'operating_cost',
  operating: 'operating_cost',
  operations: 'operating_cost'
}

const MULTI_WORD_ROLE_LABELS = new Set(
  Object.keys(ROLE_SYNONYMS).filter(r => r.includes(' '))
)

// Accept array | "purchase,inventory" | "purchase inventory" | single word
const normalizeRoleLabels = (...sources) => {
  const out = []
  const push = v => {
    if (v == null) return
    if (Array.isArray(v)) return v.forEach(push)
    String(v).split(',').forEach(part => {
      const t = part.trim()
      if (!t) return
      const lower = t.toLowerCase()
      if (MULTI_WORD_ROLE_LABELS.has(lower)) return out.push(t)
      if (t.includes(' ')) return t.split(/\s+/).forEach(p => p && out.push(p))
      out.push(t)
    })
  }
  sources.forEach(push)
  const seen = new Set()
  const labels = []
  out.forEach(x => { const lx = x.toLowerCase(); if (!seen.has(lx)) { seen.add(lx); labels.push(x) } })
  return labels
}

// ---- App --------------------------------------------------------------------
const parseIdleTimeoutMs = () => {
  const raw = Number(import.meta.env.VITE_IDLE_TIMEOUT_MINUTES || 30)
  const minutes = Number.isFinite(raw) && raw > 0 ? raw : 30
  return Math.max(60_000, minutes * 60_000)
}

const IDLE_TIMEOUT_MS = parseIdleTimeoutMs()
const ACTIVITY_EVENTS = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart', 'pointerdown']
const ACTIVITY_LISTENER_OPTIONS = { passive: true, capture: true }
const LAST_ACTIVITY_KEY = 'asysLastActivityAt'
const IDLE_LOGOUT_KEY = 'asysIdleLogoutAt'
// Opened from the phone app's print link (?print=1): this browser was just
// signed in, so it's active now, whatever it last saw (an old Safari visit).
try {
  if (new URLSearchParams(window.location.search).get('print') === '1') localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))
} catch { }

/** Runs once: back from a client's books to the practice page asked for. */
function BackToPractice({ onBack }) {
  const { pathname, search } = useLocation()
  useEffect(() => { onBack(pathname + search) }, []) // eslint-disable-line react-hooks/exhaustive-deps
  return null
}

export default function App() {
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return localStorage.getItem('sideCollapsed') === '1' } catch { return false }
  })
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [binTracking, setBinTracking] = useState(false)
  // Customer measurements (made to measure): a company switch in Settings.
  const [measurements, setMeasurements] = useState(false)
  const [configReady, setConfigReady] = useState(false)
  const [user, setUser] = useState(null)
  const [sessionReady, setSessionReady] = useState(false)
  const [companyName, setCompanyName] = useState('')

  // Build roles deterministically (lowercase + synonyms, no extra splitting)
  const roleLabels = normalizeRoleLabels(user?.roles, user?.role)
  const baseRoles = roleLabels.map(r => String(r || '').trim().toLowerCase()).filter(Boolean)
  const roles = Array.from(new Set(baseRoles.flatMap(r => [r, ROLE_SYNONYMS[r]].filter(Boolean))))
  // The company's plan: which modules it has. Unknown (an old session) means everything.
  const modules = Array.isArray(user?.modules) ? user.modules : null
  const has = useCallback((m) => hasModule(modules, m), [modules])
  // A cashier reaches the POS only (free on every plan).
  const cashierOnlyUser = baseRoles.length > 0 && baseRoles.every((r) => r === 'cashier')
  useEffect(() => { setPlanModules(modules) }, [modules])
  // A page whose module is off shows why instead.
  const gate = (mod, page) => (has(mod) ? page : <NotInPlan module={mod} />)

  // An accounting practice (its own pages only), or an accountant in a
  // client's books from their practice (api/practice.py).
  const isPractice = user?.kind === 'practice'
  const visit = user?.practice || null

  const nav = useNavigate()
  const queryClient = useQueryClient()
  useCreateOptions() // "+ Add a new …" in Master Files dropdowns
  const location = useLocation()
  const [poPending, setPoPending] = useState(0)
  const [valPending, setValPending] = useState(0)
  const [countPending, setCountPending] = useState(0)
  const idleTimerRef = useRef(null)
  const idleLogoutRef = useRef(false)
  const lastActivityBroadcastRef = useRef(0)
  const isCollapsed = !isMobile && navCollapsed

  // Layout + remember collapse
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 900
      setIsMobile(mobile)
      if (!mobile) setDrawerOpen(false)
    }
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  useEffect(() => {
    try { localStorage.setItem('sideCollapsed', navCollapsed ? '1' : '0') } catch { }
  }, [navCollapsed])
  // The phone drawer closes once a page is chosen.
  useEffect(() => { setDrawerOpen(false) }, [location.pathname])

  // Config flags, once someone is signed in (the settings API needs a session),
  // and again whenever Settings are saved.
  const loadConfig = useCallback(() => getConfig().then(c => {
    setBinTracking(
      c?.bin_tracking === true ||
      c?.bin_tracking === 1 ||
      c?.bin_tracking === '1' ||
      c?.bin_tracking === 'true' ||
      c?.bin_tracking === 'yes' ||
      c?.bin_tracking === 'on'
    )
    setMeasurements(c?.measurements === true)
    if (c?.time_zone) setCompanyZone(c.time_zone)
  }).catch(() => { }).finally(() => setConfigReady(true)), [])
  useEffect(() => {
    if (!user) { setBinTracking(false); setMeasurements(false); setConfigReady(false); return }
    loadConfig()
  }, [user?.user_id, loadConfig])

  // A company with no clock of its own yet (no time zone chosen, and a
  // currency used in several zones, such as dollars) takes the time zone of
  // the first admin's device that signs in; the API decides, and refuses an
  // accountant's visit or the ASYS team signed in as the company.
  useEffect(() => {
    if (!user?.user_id || user.time_zone_source !== 'none' || !(user.roles || []).includes('admin')) return
    const zone = deviceZone()
    if (!zone || zone === 'UTC') return
    timeZoneFromDevice(zone).then((r) => {
      if (!r?.saved) return
      setCompanyZone(r.time_zone)
      setUser((prev) => (prev && prev.user_id === user.user_id ? { ...prev, time_zone: r.time_zone, time_zone_source: r.time_zone_source } : prev))
    }).catch(() => { })
  }, [user?.user_id, user?.time_zone_source])

  // Session load (normalize roles here!)
  useEffect(() => {
    (async () => {
      try {
        const u = await getMe()
        if (u && u.user_id) {
          setCurrency(u.currency)
          setCompanyZone(u.time_zone)
          const normalized = normalizeRoleLabels(u.roles, u.role)
          setUser({ ...u, roles: normalized, role: u.role ?? (normalized[0] ?? '') })
        }
      } catch { }
      finally { setSessionReady(true) }
    })()
  }, [])

  // The plan can change while this tab is open (in the control panel, or by
  // another admin). When the server refuses a part as not in the plan, read
  // the plan again so the menu and pages follow it. At most every 5 seconds,
  // and only a real change re-renders.
  const planCheckRef = useRef(0)
  useEffect(() => {
    if (!user?.user_id) return undefined
    const onPlanChanged = async () => {
      if (Date.now() - planCheckRef.current < 5000) return
      planCheckRef.current = Date.now()
      try {
        const u = await getMe()
        setUser((prev) => {
          if (!prev || !u || u.user_id !== prev.user_id) return prev
          if (u.plan === prev.plan && String(u.modules) === String(prev.modules)) return prev
          const normalized = normalizeRoleLabels(u.roles, u.role)
          return { ...u, roles: normalized, role: u.role ?? (normalized[0] ?? '') }
        })
      } catch { }
    }
    window.addEventListener('asys:plan-changed', onPlanChanged)
    return () => window.removeEventListener('asys:plan-changed', onPlanChanged)
  }, [user?.user_id])

  // A client removed its accountant (or the practice took the client away):
  // the server has already put the session back in the practice.
  useEffect(() => {
    if (!user?.user_id) return undefined
    const onPracticeChanged = async (event) => {
      try {
        const u = await getMe()
        signIn(u)
      } catch { }
      nav('/practice', { replace: true, state: { notice: event?.detail?.message } })
    }
    window.addEventListener('asys:practice-changed', onPracticeChanged)
    return () => window.removeEventListener('asys:practice-changed', onPracticeChanged)
  }, [user?.user_id])

  const logoutForIdle = useCallback(async () => {
    if (idleLogoutRef.current) return
    idleLogoutRef.current = true
    try {
      localStorage.setItem(IDLE_LOGOUT_KEY, String(Date.now()))
    } catch { }
    try { await apiLogout() } catch { }
    setUser(null)
    nav('/login?reason=timeout', { replace: true })
    idleLogoutRef.current = false
  }, [nav])

  useEffect(() => {
    if (!user) return undefined

    const resetIdleTimer = (nextDelayMs) => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      let remaining = nextDelayMs
      if (remaining == null) {
        const now = Date.now()
        const lastSeen = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || now)
        const elapsed = Math.max(0, now - lastSeen)
        remaining = Math.max(0, IDLE_TIMEOUT_MS - elapsed)
      }
      idleTimerRef.current = setTimeout(logoutForIdle, remaining)
    }

    const markActivity = ({ force = false } = {}) => {
      const now = Date.now()
      const previous = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || now)
      if (!force && now - previous >= IDLE_TIMEOUT_MS) {
        void logoutForIdle()
        return
      }
      if (force || now - lastActivityBroadcastRef.current > 15_000) {
        lastActivityBroadcastRef.current = now
        try { localStorage.setItem(LAST_ACTIVITY_KEY, String(now)) } catch { }
      }
      resetIdleTimer(IDLE_TIMEOUT_MS)
    }

    const onStorage = (event) => {
      if (event.key === IDLE_LOGOUT_KEY) {
        setUser(null)
        nav('/login?reason=timeout', { replace: true })
        return
      }
      if (event.key === LAST_ACTIVITY_KEY) resetIdleTimer()
    }
    const onSessionExpired = () => {
      void logoutForIdle()
    }

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, markActivity, ACTIVITY_LISTENER_OPTIONS)
    })
    window.addEventListener('storage', onStorage)
    window.addEventListener('asys:session-expired', onSessionExpired)
    const now = Date.now()
    const lastSeen = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || now)
    if (now - lastSeen >= IDLE_TIMEOUT_MS) {
      void logoutForIdle()
    } else if (!localStorage.getItem(LAST_ACTIVITY_KEY)) {
      markActivity({ force: true })
    } else {
      resetIdleTimer()
    }

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, markActivity, ACTIVITY_LISTENER_OPTIONS)
      })
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('asys:session-expired', onSessionExpired)
    }
  }, [logoutForIdle, nav, user])

  // Admin badges
  useEffect(() => {
    let timer
    const run = async () => {
      try {
        if (roles.includes('admin')) {
          // Each badge on its own, and only for modules in the plan, so one
          // refused call doesn't blank the others.
          try {
            const rows = has('purchase') ? await scanPOApprovals() : []; setPoPending(Array.isArray(rows) ? rows.length : 0)
          } catch { setPoPending(0) }
          try {
            const v = has('inventory') ? await listValuationApprovals() : []; setValPending(Array.isArray(v) ? v.length : 0)
          } catch { setValPending(0) }
          try {
            const c = has('inventory') ? await listCounts('waiting') : []; setCountPending(Array.isArray(c) ? c.length : 0)
          } catch { setCountPending(0) }
        } else { setPoPending(0); setValPending(0); setCountPending(0) }
      } catch { setPoPending(0); setValPending(0); setCountPending(0) }
      timer = setTimeout(run, 60000)
    }
    run()
    return () => { if (timer) clearTimeout(timer) }
  }, [roles.join('|'), has])

  // Company name for the header, once someone is signed in.
  const loadCompanyName = useCallback(() => getCompany().then(c => setCompanyName(c?.company_name || '')).catch(() => { }), [])
  useEffect(() => {
    if (!user) { setCompanyName(''); return }
    loadCompanyName()
  }, [user?.user_id, loadCompanyName])

  const photoBase = import.meta.env.VITE_API_URL || ''
  const photoSrc = user?.photo_url
    ? (String(user.photo_url).startsWith('http') ? user.photo_url : photoBase + user.photo_url)
    : ''
  const userLabel = user?.full_name || user?.username || ''
  const initials = userLabel
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0])
    .join('')
    .toUpperCase()
  const hideShell = ['/', '/login', '/signup', '/forgot-password', '/accountants', '/accountants/signup'].includes(location.pathname) || location.pathname.startsWith('/reset-password')
    || location.pathname.startsWith('/accountant-invite/')
    || location.pathname === '/reset-password'
    || location.pathname.startsWith('/reset-password/')
  const signIn = (u) => {
    // Another company (or none): nothing cached from the last one is shown.
    queryClient.clear()
    if (!u) return setUser(null)
    closeAllNavGroups()
    setCurrency(u.currency)
    setCompanyZone(u.time_zone)
    const normalized = normalizeRoleLabels(u.roles, u.role)
    setUser({ ...u, roles: normalized, role: u.role ?? (normalized[0] ?? '') })
  }
  const signOut = async () => {
    try { await apiLogout(); queryClient.clear(); setUser(null); nav('/login') } catch { }
  }
  // Back from a client's books to the practice: its Clients list, or the
  // practice page asked for (from the phone app's More).
  const backToClients = async (to = '/practice') => {
    try { await backToPractice() } catch { }
    try { signIn(await getMe()) } catch { }
    nav(typeof to === 'string' ? to : '/practice', { replace: true })
  }

  const isAdmin = roles.includes('admin')
  const approvals = {
    po: isAdmin ? poPending : 0,
    valuation: isAdmin ? valPending : 0,
    counts: isAdmin ? countPending : 0,
    inventory: isAdmin ? valPending + countPending : 0, // the Inventory badge in the side nav
  }
  // Settings pages call this after saving so the header and bin pages update.
  const refreshSettings = () => Promise.all([loadConfig(), loadCompanyName()])
  // An accountant with View only: pages hide what would change the books
  // (buttons marked data-write, see .layout.view-only), and the server refuses it.
  const readOnly = visit?.access === 'view'
  const session = { user, roles, approvals, binTracking, measurements, modules, has, refreshSettings, readOnly }
  // Bin pages exist only with bin tracking on; wait for the setting before
  // deciding, so a direct load doesn't bounce away.
  const binPage = (page) => (binTracking ? page : (configReady ? <Navigate to="/inventory" replace /> : null))

  if (hideShell) {
    return (
      <div className="layout auth-layout">
        <div className="auth-main">{renderRoutes()}</div>
      </div>
    )
  }

  // Inside the phone app: it has its own tabs and header, so just the page
  // (from the first moment, so the web header never flashes up). An
  // accountant in a client's books gets a slim bar to go back to Clients.
  if (IN_APP) {
    return (
      <div className={`layout in-app${visit ? ' visiting' : ''}${readOnly ? ' view-only' : ''}`}>
        {visit && (
          <div className={`in-app-visit${visit.access === 'view' ? ' view' : ''}`}>
            <span><strong>{visit.client_name || companyName}</strong> · {visit.access === 'view' ? 'View only' : 'Full books'} · via {visit.name}</span>
            <button type="button" onClick={() => backToClients()}><ArrowLeft size={14} aria-hidden="true" />Clients</button>
          </div>
        )}
        <main className="main" key={user?.tenant_id || 'out'}>
          <SessionContext.Provider value={session}><ErrorBoundary resetKey={location.pathname}>{isPractice ? renderPracticeRoutes() : renderRoutes()}</ErrorBoundary></SessionContext.Provider>
        </main>
      </div>
    )
  }

  // App shell, after PropertyBuk and ASYS Tax: a slim header across the top
  // with the brand, company and account, and the grouped sidebar below it.
  return (
    <div className={`layout${isCollapsed ? ' nav-collapsed' : ''}${user && !isPractice ? '' : ' no-nav'}${readOnly ? ' view-only' : ''}`}>
      <header className={`app-header${visit ? ' visiting' : ''}`}>
        <div className="app-brand">
          {isPractice ? null : isMobile ? (
            <button
              className="header-icon-btn"
              type="button"
              aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(o => !o)}
            >
              <Menu size={18} />
            </button>
          ) : (
            <button
              className="header-icon-btn"
              type="button"
              aria-label={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
              aria-expanded={!navCollapsed}
              onClick={() => setNavCollapsed(c => !c)}
            >
              {navCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>
          )}
          <Link to={user ? (isPractice ? '/practice' : '/dashboard') : '/'} className="app-brand-link">
            <span className="app-brand-mark" aria-hidden="true"><Store size={16} /></span>
            <span className="app-brand-name">ASYS Retail</span>
          </Link>
          {(visit?.client_name || companyName) && <span className="app-brand-org">{visit?.client_name || companyName}</span>}
          {visit && (
            <>
              <span className={`app-visit${visit.access === 'view' ? ' view' : ''}`} title={`You’re in ${visit.client_name || companyName || 'this company'}’s books as their accountant from ${visit.name}.`}>
                <span className="app-visit-firm">via {visit.name} · </span>{visit.access === 'view' ? 'View only' : 'Full books'}
              </span>
              <button type="button" className="app-visit-back" onClick={() => backToClients()} aria-label="Back to your clients"><ArrowLeft size={14} aria-hidden="true" /><span className="app-visit-back-label">Clients</span></button>
            </>
          )}
          {user && roles.includes('admin') && user.trial_days_left != null && (
            <Link to="/settings/plan" className={`app-trial${user.trial_days_left < 0 ? ' ended' : ''}`} title={`Free trial of ${user.plan_label || 'your plan'}${user.trial_ends_at ? `, until ${user.trial_ends_at}` : ''}. See your plan.`}>
              {user.trial_days_left < 0 ? 'Trial ended · contact ASYS' : `Trial: ${user.trial_days_left} ${user.trial_days_left === 1 ? 'day' : 'days'} left`}
            </Link>
          )}
        </div>
        <div className="app-user">
          {user ? (
            <>
              <Link to={`/master/users/${user.user_id}`} className="app-account" title="Your profile">
                <span className="app-avatar" aria-hidden="true">
                  {photoSrc ? <img src={photoSrc} alt="" /> : (initials || 'U')}
                </span>
                <span className="app-account-name">{userLabel}</span>
              </Link>
              <button className="app-signout" type="button" onClick={signOut}>
                <LogOut size={16} aria-hidden="true" /><span>Sign out</span>
              </button>
            </>
          ) : sessionReady && (
            <Link className="app-signout" to="/login">Log in</Link>
          )}
        </div>
      </header>

      {user && !isPractice && (
        <SideNav
          roles={roles}
          modules={modules}
          counts={approvals}
          collapsed={isCollapsed}
          open={isMobile && drawerOpen}
        />
      )}
      {isMobile && drawerOpen && <div className="nav-backdrop" onClick={() => setDrawerOpen(false)} />}

      <main className="main" key={user?.tenant_id || 'out'}>
        <SessionContext.Provider value={session}><ErrorBoundary resetKey={location.pathname}>{isPractice ? renderPracticeRoutes() : renderRoutes()}</ErrorBoundary></SessionContext.Provider>
      </main>
    </div>
  )

  // A practice has no books of its own: its clients, and (for its admins)
  // its staff and settings.
  function renderPracticeRoutes() {
    const admin = roles.includes('admin')
    return (
      <Routes>
        <Route path="/practice" element={<Clients user={user} onSwitched={signIn} />} />
        {admin && <Route path="/practice/clients/new" element={<AddClient />} />}
        {admin && <Route path="/practice/staff" element={<Staff />} />}
        {admin && <Route path="/practice/staff/new" element={<StaffForm />} />}
        {admin && <Route path="/practice/staff/:userId" element={<StaffForm />} />}
        {admin && <Route path="/practice/settings" element={<CompanySettings practice />} />}
        <Route path="/master/users/:userId" element={<UserDetail />} />
        <Route path="*" element={<Navigate to="/practice" replace />} />
      </Routes>
    )
  }

  function renderRoutes() {
    return (
        <Routes>
          <Route
            path="/"
            element={
              sessionReady
                ? (user ? <Navigate to="/dashboard" replace /> : <Home />)
                : <Home />
            }
          />

          <Route
            path="/login"
            element={
              <Login
                onSignedIn={signIn}
              />
            }
          />
          <Route
            path="/signup"
            element={
              <Signup
                onSignedIn={signIn}
              />
            }
          />
          {/* Pricing is on the landing page now; old links still land on it. */}
          <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/accountants" element={<ForAccountants />} />
          {/* An accountant in a client's books going to a practice page
              (the phone app's Clients tab and More): back to the practice first. */}
          {visit && <Route path="/practice/*" element={<BackToPractice onBack={backToClients} />} />}
          <Route path="/accountants/signup" element={<PracticeSignup onSignedIn={signIn} />} />
          <Route path="/accountant-invite/:token" element={<InviteAccept user={sessionReady ? user : undefined} onSignedIn={signIn} />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Onboarding Wizard */}
          <Route path="/onboarding" element={<OnboardingPage />} />

          <Route
            path="/dashboard"
            element={
              sessionReady
                ? (user
                  ? (cashierOnlyUser ? <Navigate to="/sales/pos" replace /> : <Dashboard roles={roles} approvals={approvals} />)
                  : <Navigate to="/login?next=/dashboard" replace />)
                : <div className="page"><p className="muted">Loading session...</p></div>
            }
          />
          {(roles.includes('admin') || roles.includes('accounting') || roles.includes('purchase') || roles.includes('inventory')) && (
            <Route path="/dashboard/analysis" element={<Analysis />} />
          )}

          {(roles.includes('admin') || roles.includes('purchase') || roles.includes('inventory')) && (
            <Route path="/import" element={<ImportMasters />} />
          )}

          <Route path="/master" element={<MasterMenu />} />
          {roles.includes('admin') && (
            <>
              <Route path="/master/accounts" element={gate('accounting', <AccountList />)} />
              <Route path="/master/accounts/new" element={gate('accounting', <AccountForm />)} />
              <Route path="/master/accounts/:accountId" element={gate('accounting', <AccountForm />)} />
              <Route path="/master/opening-balances" element={gate('accounting', <OpeningBalances />)} />
              <Route path="/master/setup" element={gate('accounting', <AccountSetup />)} />
              <Route path="/setup" element={gate('accounting', <AccountSetup />)} />
            </>
          )}
          {(roles.includes('admin') || roles.includes('sales') || roles.includes('purchase') || roles.includes('inventory')) && (
            <>
              <Route path="/master/products" element={<ItemList />} />
              <Route path="/master/products/new" element={<ItemForm />} />
              <Route path="/master/products/:itemId" element={<ItemForm />} />
            </>
          )}
          {(roles.includes('admin') || roles.includes('purchase') || roles.includes('inventory')) && (
            <>
              <Route path="/master/vendors" element={<VendorList />} />
              <Route path="/master/vendors/new" element={<VendorForm />} />
              <Route path="/master/vendors/:vendorId" element={<VendorForm />} />
            </>
          )}
          {roles.includes('admin') && (
            <>
              <Route path="/master/locations" element={<LocationList />} />
              <Route path="/master/locations/new" element={<LocationForm />} />
              <Route path="/master/locations/:locationId" element={<LocationForm />} />
            </>
          )}
          {(roles.includes('admin') || roles.includes('sales')) && (
            <>
              <Route path="/master/customers" element={<CustomerList />} />
              <Route path="/master/customers/new" element={<CustomerForm />} />
              <Route path="/master/customers/:customerId" element={<CustomerForm />} />
            </>
          )}
          {roles.includes('admin') && <Route path="/master/control-accounts" element={gate('accounting', <DefaultAccounts />)} />}
          {roles.includes('admin') && (
            <>
              <Route path="/master/users" element={<UserList />} />
              <Route path="/master/users/new" element={<UserForm />} />
              <Route path="/master/users/:userId/edit" element={<UserForm />} />
            </>
          )}
          {user && <Route path="/master/users/:userId" element={<UserDetail />} />}

          {(roles.includes('admin') || roles.includes('operating_cost')) && (
            <>
              <Route path="/operating" element={gate('expenses', <OperatingMenu />)} />
              <Route path="/operating/expenses" element={gate('expenses', <ExpenseList />)} />
              <Route path="/operating/expenses/:jeId" element={gate('expenses', <ExpenseDetail />)} />
              <Route path="/operating/new" element={gate('expenses', <ExpenseForm />)} />
              <Route path="/operating/sga" element={gate('expenses', <Navigate to="/operating/new" replace />)} />
              <Route path="/operating/lines" element={gate('expenses', <Navigate to="/operating/expenses" replace />)} />
            </>
          )}

          {/* Purchase */}
          <Route path="/purchase" element={!has('purchase') && has('invoices') ? <Navigate to="/purchase/invoices" replace /> : gate('purchase', <PurchaseMenu />)} />
          <Route path="/purchase/po-list" element={gate('purchase', <POList />)} />
          <Route path="/purchase/po-form/new" element={gate('purchase', <POForm />)} />
          <Route path="/purchase/po-detail/:poId" element={gate('purchase', <PODetail />)} />
          <Route path="/purchase/receipts" element={gate('purchase', <ReceiptList />)} />
          <Route path="/purchase/receipts/new" element={gate('purchase', <ReceiptForm />)} />
          <Route path="/purchase/receipts/:id" element={gate('purchase', <ReceiptDetail />)} />
          <Route path="/purchase/vendor-returns" element={gate('purchase', <VendorReturnList />)} />
          <Route path="/purchase/vendor-returns/new" element={gate('purchase', <VendorReturnForm />)} />
          <Route path="/purchase/stock-value" element={gate('purchase', <StockValue />)} />
          <Route path="/purchase/vendor-returns/:id" element={gate('purchase', <VendorReturnDetail />)} />
          <Route path="/purchase/invoices" element={gate('purchase|invoices', <InvoiceList />)} />
          <Route path="/purchase/invoices/new" element={gate('purchase|invoices', <InvoiceForm />)} />
          <Route path="/purchase/invoices/:id" element={gate('purchase|invoices', <InvoiceDetail />)} />
          {roles.includes('admin') && <Route path="/purchase/po-approval-inbox" element={gate('purchase', <POApprovalInbox />)} />}
          {roles.includes('admin') && <Route path="/purchase/approval-settings" element={gate('purchase', <ApprovalSettings />)} />}
          <Route path="/purchase/ap-ledger" element={gate('purchase|invoices', <APLedger />)} />
          <Route path="/purchase/ap/vendor/:vendorId" element={gate('purchase|invoices', <APVendorDetail />)} />
          <Route path="/purchase/ap/payment" element={gate('purchase|invoices', <APPayment />)} />

          {/* Sales */}
          <Route path="/sales" element={!has('pos|invoices|jobs') ? <NotInPlan module="pos" /> : paymentsOnly(modules) ? <Navigate to="/sales/customer-payments" replace /> : <SalesMenu roles={roles} />} />
          <Route path="/sales/pos" element={gate('pos', <POS />)} />
          <Route path="/sales/list" element={gate('pos', <SaleList />)} />
          <Route path="/sales/refund-lookup" element={gate('pos', <RefundLookup />)} />
          <Route path="/sales/refund/:saleId" element={gate('pos', <RefundProcess />)} />
          <Route path="/sales/detail/:saleId" element={gate('pos', <SaleDetail />)} />
          <Route path="/sales/quotes" element={gate('invoices|jobs', <CustomerQuoteList />)} />
          {/* Jobs: their own section, since a job gathers sales, purchases and expenses. */}
          <Route path="/jobs" element={gate('jobs', <JobList />)} />
          <Route path="/jobs/new" element={gate('jobs', <JobDetail />)} />
          <Route path="/jobs/:jobId" element={gate('jobs', <JobDetail />)} />
          <Route path="/jobs/:jobId/cutting-list" element={gate('jobs', <CuttingList />)} />
          <Route path="/sales/jobs" element={<Navigate to="/jobs" replace />} />
          <Route path="/sales/jobs/:jobId" element={<OldJobLink />} />
          <Route path="/sales/quotes/new" element={gate('invoices|jobs', <CustomerQuoteForm />)} />
          <Route path="/sales/quotes/:quoteId" element={gate('invoices|jobs', <CustomerQuoteForm />)} />
          <Route path="/sales/customer-invoices" element={gate('invoices|jobs', <CustomerInvoiceList />)} />
          <Route path="/sales/customer-invoices/new" element={gate('invoices|jobs', <CustomerInvoiceForm />)} />
          <Route path="/sales/customer-invoices/:invoiceId" element={gate('invoices|jobs', <CustomerInvoiceForm />)} />
          <Route path="/sales/customer-payments" element={gate('invoices|jobs|pos', <CustomerPayment />)} />

          {/* Inventory */}
          <Route path="/inventory" element={gate('inventory', <InventoryMenu />)} />
          <Route path="/inventory/stock" element={gate('inventory', <StockList />)} />
          <Route path="/inventory/materials" element={gate('production', <StockList materials />)} />
          <Route path="/inventory/stock/:productId" element={gate('inventory', <ProductStock />)} />
          <Route path="/inventory/adjust" element={gate('inventory', <AdjustStock />)} />
          <Route path="/inventory/move" element={gate('inventory', <MoveStock />)} />
          <Route path="/inventory/movements" element={gate('inventory', <StockMovements />)} />
          <Route path="/inventory/movements/new" element={gate('inventory', <Navigate to="/inventory/move" replace />)} />
          <Route path="/inventory/product-lookup" element={gate('inventory', <Navigate to="/inventory/stock" replace />)} />
          <Route path="/inventory/items" element={gate('inventory', <Navigate to="/inventory/stock" replace />)} />
          <Route path="/inventory/cost-layers" element={gate('inventory', <CostLayers />)} />
          <Route path="/inventory/production" element={gate('production', <Production />)} />
          <Route path="/inventory/production/products" element={gate('production', <ProductionProducts />)} />
          <Route path="/inventory/production/cogm" element={gate('production', <ProductionCogm />)} />
          <Route path="/inventory/production/:runId" element={gate('production', <ProductionRun />)} />
          <Route path="/inventory/cost-layers-summary" element={gate('inventory', <CostLayerSummary />)} />
          <Route path="/inventory/counts" element={gate('inventory', <StockCounts />)} />
          <Route path="/inventory/counts/:countId" element={gate('inventory', <StockCountDetail />)} />
          <Route path="/inventory/valuation" element={gate('inventory', <Navigate to="/inventory/adjust" replace />)} />
          <Route path="/inventory/valuation-nrv" element={gate('inventory', <Navigate to="/inventory/adjust" replace />)} />
          <Route path="/inventory/valuation-ops" element={gate('inventory', <Navigate to="/inventory/adjust" replace />)} />
          {roles.includes('admin') && <Route path="/inventory/valuation-approvals" element={gate('inventory', <ValuationApprovals />)} />}
          {roles.includes('admin') && <Route path="/inventory/approval-settings" element={<Navigate to="/inventory/valuation-approvals" replace />} />}
          <Route path="/inventory/areas" element={gate('inventory', binPage(<StorageAreas />))} />
          <Route path="/inventory/sku-bins" element={gate('inventory', binPage(<BinStockBySku />))} />
          <Route path="/inventory/bin-move-sku" element={gate('inventory', binPage(<BinMoveBySku />))} />
          <Route path="/inventory/bin-adjust-sku" element={gate('inventory', binPage(<BinAdjustBySku />))} />

          {/* Accounting */}
          {(roles.includes('admin') || roles.includes('accounting')) && (<>
            <Route path="/accounting" element={gate('accounting', <AccountingMenu />)} />
            <Route path="/accounting/journal-entries" element={gate('accounting', <JournalEntries />)} />
            <Route path="/accounting/reports" element={gate('accounting', <ReportsMenu />)} />
            <Route path="/accounting/journal/:jeId" element={gate('accounting', <JournalEntryDetail />)} />
            <Route path="/accounting/transaction-types" element={gate('accounting', <TransactionTypes />)} />
            <Route path="/accounting/transaction-types/new" element={gate('accounting', <TransactionTypeForm />)} />
            <Route path="/accounting/transaction-types/record" element={gate('accounting', <RecordTransaction />)} />
            <Route path="/accounting/transaction-types/:typeId" element={gate('accounting', <TransactionTypeForm />)} />
            <Route path="/accounting/reports/trial-balance" element={gate('accounting', <TrialBalance />)} />
            <Route path="/accounting/reports/classification" element={gate('accounting', <StatementClasses />)} />
            <Route path="/accounting/reports/general-ledger" element={gate('accounting', <GeneralLedger />)} />
            <Route path="/accounting/reports/balance-sheet" element={gate('accounting', <BalanceSheet />)} />
            <Route path="/accounting/reports/income-statement" element={gate('accounting', <IncomeStatement />)} />
            <Route path="/accounting/reports/period-package" element={gate('accounting', <PeriodReportPrint />)} />
            <Route path="/accounting/reports/ap-subledger" element={gate('accounting', <APSubledger />)} />
            <Route path="/accounting/reports/ar-subledger" element={gate('accounting', <ARSubledger />)} />
            <Route path="/accounting/reports/jobs-in-progress" element={gate(has('accounting') ? 'jobs' : 'accounting', <JobsInProgress />)} />
            <Route path="/accounting/ap/matching" element={gate('accounting', <VendorInvoiceMatching />)} />
            <Route path="/accounting/ap/invoices/:id" element={gate('accounting', <InvoiceDetail />)} />
            <Route path="/accounting/ap/ledger" element={gate('accounting', <APLedger />)} />
            <Route path="/accounting/ap/payment" element={gate('accounting', <APPayment />)} />
            <Route path="/accounting/ap/vendor/:vendorId" element={gate('accounting', <APVendorDetail />)} />
            <Route path="/accounting/month-end" element={gate('accounting', <MonthEnd />)} />
            <Route path="/accounting/tieout" element={gate('accounting', <Navigate to="/accounting/month-end" replace />)} />
            <Route path="/accounting/bank-rec" element={gate('accounting', <BankRec />)} />
            <Route path="/accounting/inv-cutoff" element={gate('accounting', <Navigate to="/accounting/month-end" replace />)} />
            <Route path="/accounting/period-close" element={gate('accounting', <Navigate to="/accounting/month-end" replace />)} />
            <Route path="/accounting/assets" element={gate('accounting', <Assets />)} />
            <Route path="/accounting/assets/new" element={gate('accounting', <AssetForm />)} />
            <Route path="/accounting/assets/:assetId" element={gate('accounting', <AssetDetail />)} />
            <Route path="/accounting/prepaids" element={gate('accounting', <PrepaidList />)} />
            <Route path="/accounting/prepaids/new" element={gate('accounting', <PrepaidForm />)} />
          </>)}

          {/* Settings */}
          {(roles.includes('admin') || roles.includes('accounting')) && <Route path="/settings" element={<SettingsMenu />} />}
          {roles.includes('admin') && <Route path="/settings/company" element={<CompanySettings />} />}
          {roles.includes('admin') && <Route path="/settings/plan" element={<PlanSettings />} />}
          {roles.includes('admin') && <Route path="/settings/accountant" element={<AccountantSettings />} />}
          {(roles.includes('admin') || roles.includes('accounting')) && <Route path="/settings/mono" element={<MonoConnections />} />}
          {(roles.includes('admin') || roles.includes('accounting')) && <Route path="/settings/payments" element={<Payments />} />}

          {/* Utilities */}
          <Route path="/utilities/audit" element={<AuditTrail />} />
          {roles.includes('admin') && <Route path="/settings/data" element={<DataTools />} />}
          {/* Older addresses for the two Data tools (the setup wizard still uses /utilities/setup). */}
          {roles.includes('admin') && <Route path="/utilities/reset" element={<Navigate to="/settings/data" replace />} />}
          {roles.includes('admin') && <Route path="/utilities/setup" element={<Navigate to="/settings/data" replace />} />}
          {roles.includes('admin') && <Route path="/platform/tenants" element={<PlatformTenants />} />}

          {/* catch-all → dashboard */}
          {/* Role-gated routes only exist once the session has loaded, so wait
              for it before redirecting; otherwise a refresh loses the page. */}
          <Route
            path="*"
            element={
              sessionReady
                ? <Navigate to={user ? '/dashboard' : '/'} replace />
                : <div className="page"><p className="muted">Loading session...</p></div>
            }
          />
        </Routes>
    )
  }
}
